import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button, TextField, Slider, Box, Typography, MenuItem, Select, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import CropIcon from '@mui/icons-material/Crop';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import FlipIcon from '@mui/icons-material/Flip';
import Brightness6Icon from '@mui/icons-material/Brightness6';
import ContrastIcon from '@mui/icons-material/Tonality';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import OpacityIcon from '@mui/icons-material/Opacity';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_IMAGE = '';

const ASPECT_RATIOS = [
  { label: 'Original', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
  { label: 'Custom', value: 'custom' },
];

const FONT_FAMILIES = [
  'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana', 'Roboto', 'Montserrat', 'Lobster', 'Oswald', 'Comic Sans MS'
];

const EDIT_OPTIONS = [
  { key: 'undo', label: 'Undo', icon: <RotateLeftIcon /> },
  { key: 'redo', label: 'Redo', icon: <RotateLeftIcon style={{ transform: 'scaleX(-1)' }} /> },
  { key: 'aspect', label: 'Aspect', icon: <FormatSizeIcon /> },
  { key: 'crop', label: 'Crop', icon: <CropIcon /> },
  { key: 'rotate', label: 'Rotate', icon: <RotateLeftIcon /> },
  { key: 'flip', label: 'Flip', icon: <FlipIcon /> },
  { key: 'brightness', label: 'Brightness', icon: <Brightness6Icon /> },
  { key: 'contrast', label: 'Contrast', icon: <ContrastIcon /> },
  { key: 'blur', label: 'Blur', icon: <BlurOnIcon /> },
  { key: 'opacity', label: 'Opacity', icon: <OpacityIcon /> },
  { key: 'grayscale', label: 'Grayscale', icon: <FilterVintageIcon /> },
  { key: 'saturate', label: 'Saturation', icon: <ColorLensIcon /> },
  { key: 'text', label: 'Text', icon: <FormatColorTextIcon /> },
  { key: 'border', label: 'Border', icon: <FormatSizeIcon /> },
  { key: 'shadow', label: 'Shadow', icon: <BlurOnIcon /> },
  { key: 'round', label: 'Round Corners', icon: <FontDownloadIcon /> },
  { key: 'sticker', label: 'Stickers', icon: <ColorLensIcon /> },
  { key: 'frame', label: 'Frame', icon: <FilterVintageIcon /> },
  { key: 'watermark', label: 'Watermark', icon: <OpacityIcon /> },
  { key: 'hashtag', label: 'Hashtag', icon: <FormatColorTextIcon /> },
  { key: 'logo', label: 'Logo', icon: <FontDownloadIcon /> },
];

const PhotoEditor = () => {
  const [imageUrl, setImageUrl] = useState(DEFAULT_IMAGE);
  const [inputUrl, setInputUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [selectedTool, setSelectedTool] = useState('crop');
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [blur, setBlur] = useState(0);
  const [opacity, setOpacity] = useState(100);
  const [grayscale, setGrayscale] = useState(0);
  const [saturate, setSaturate] = useState(100);
  const [aspect, setAspect] = useState(4 / 3);
  const [textLayers, setTextLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderColor, setBorderColor] = useState('#ffffff');
  const [boxShadow, setBoxShadow] = useState(0);
  const [borderRadius, setBorderRadius] = useState(0);
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkOpacity, setWatermarkOpacity] = useState(30);
  const [resizeWidth, setResizeWidth] = useState(600);
  const [resizeHeight, setResizeHeight] = useState(400);
  const [stickers, setStickers] = useState([]);
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  const [frameStyle, setFrameStyle] = useState('none');
  const [hashtagText, setHashtagText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPosition, setLogoPosition] = useState({ x: 90, y: 90 });
  const [logoSize, setLogoSize] = useState(60);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Undo/Redo functionality
  const getEditorState = useCallback(() => ({
    imageUrl, crop, zoom, rotation, flip, brightness, contrast, blur, opacity, 
    grayscale, saturate, aspect, textLayers, selectedLayerId, borderWidth, 
    borderColor, boxShadow, borderRadius, watermarkText, watermarkOpacity, 
    resizeWidth, resizeHeight, stickers, selectedStickerId, frameStyle, 
    hashtagText, logoUrl, logoPosition, logoSize
  }), [
    imageUrl, crop, zoom, rotation, flip, brightness, contrast, blur, opacity, 
    grayscale, saturate, aspect, textLayers, selectedLayerId, borderWidth, 
    borderColor, boxShadow, borderRadius, watermarkText, watermarkOpacity, 
    resizeWidth, resizeHeight, stickers, selectedStickerId, frameStyle, 
    hashtagText, logoUrl, logoPosition, logoSize
  ]);

  const setEditorState = useCallback((state) => {
    setImageUrl(state.imageUrl || '');
    setCrop(state.crop || { x: 0, y: 0 });
    setZoom(state.zoom || 1);
    setRotation(state.rotation || 0);
    setFlip(state.flip || { horizontal: false, vertical: false });
    setBrightness(state.brightness || 100);
    setContrast(state.contrast || 100);
    setBlur(state.blur || 0);
    setOpacity(state.opacity || 100);
    setGrayscale(state.grayscale || 0);
    setSaturate(state.saturate || 100);
    setAspect(state.aspect || 4 / 3);
    setTextLayers(state.textLayers || []);
    setSelectedLayerId(state.selectedLayerId || null);
    setBorderWidth(state.borderWidth || 0);
    setBorderColor(state.borderColor || '#ffffff');
    setBoxShadow(state.boxShadow || 0);
    setBorderRadius(state.borderRadius || 0);
    setWatermarkText(state.watermarkText || '');
    setWatermarkOpacity(state.watermarkOpacity || 30);
    setResizeWidth(state.resizeWidth || 600);
    setResizeHeight(state.resizeHeight || 400);
    setStickers(state.stickers || []);
    setSelectedStickerId(state.selectedStickerId || null);
    setFrameStyle(state.frameStyle || 'none');
    setHashtagText(state.hashtagText || '');
    setLogoUrl(state.logoUrl || '');
    setLogoPosition(state.logoPosition || { x: 90, y: 90 });
    setLogoSize(state.logoSize || 60);
  }, []);

  const pushUndo = useCallback(() => {
    const currentState = getEditorState();
    setUndoStack(stack => [...stack.slice(-19), currentState]);
    setRedoStack([]);
  }, [getEditorState]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const currentState = getEditorState();
    setRedoStack(stack => [currentState, ...stack.slice(0, 19)]);
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(stack => stack.slice(0, -1));
    setEditorState(prev);
  }, [undoStack.length, getEditorState, setEditorState]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const currentState = getEditorState();
    setUndoStack(stack => [...stack.slice(-19), currentState]);
    const next = redoStack[0];
    setRedoStack(stack => stack.slice(1));
    setEditorState(next);
  }, [redoStack.length, getEditorState, setEditorState]);

  const handleAddTextLayerAt = (x, y) => {
    pushUndo();
    const newLayer = {
      id: uuidv4(),
      text: '',
      color: '#ffffff',
      font: 'Arial',
      size: 32,
      x,
      y,
      editing: true,
    };
    setTextLayers(layers => [...layers, newLayer]);
    setSelectedLayerId(newLayer.id);
  };

  const updateLayer = (id, changes) => {
    setTextLayers(layers => layers.map(l => l.id === id ? { ...l, ...changes } : l));
  };

  const createImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });
  };

  const showCroppedImage = useCallback(async () => {
    if (!imageUrl || !croppedAreaPixels) return;
    
    try {
      // Inline generation for preview (to avoid circular dependency)
      const image = await createImage(imageUrl);
      const canvas = document.createElement('canvas');
      canvas.width = resizeWidth;
      canvas.height = resizeHeight;
      const ctx = canvas.getContext('2d');
      
      // White background for preview
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.save();
      
      // Apply transforms and filters
      if (flip.horizontal || flip.vertical) {
        ctx.translate(
          flip.horizontal ? canvas.width : 0,
          flip.vertical ? canvas.height : 0
        );
        ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
      }
      
      if (rotation !== 0) {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
      }
      
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) blur(${blur}px) opacity(${opacity}%) grayscale(${grayscale}%) saturate(${saturate}%)`;
      
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      
      ctx.restore();
      ctx.filter = 'none';
      
      // Draw overlays (simplified for preview)
      textLayers.forEach(layer => {
        if (layer.text) {
          ctx.save();
          ctx.font = `${layer.size}px ${layer.font}`;
          ctx.fillStyle = layer.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
          ctx.shadowBlur = 4;
          ctx.fillText(
            layer.text,
            (layer.x / 100) * canvas.width,
            (layer.y / 100) * canvas.height
          );
          ctx.restore();
        }
      });
      
      // Clean up previous URL
      if (croppedImage) {
        URL.revokeObjectURL(croppedImage);
      }
      
      canvas.toBlob((blob) => {
        if (blob) {
          setCroppedImage(URL.createObjectURL(blob));
        }
      }, 'image/jpeg', 0.9);
      
    } catch (error) {
      console.error('Error creating cropped image:', error);
      alert('Error processing image. Please try again.');
    }
  }, [
    imageUrl, croppedAreaPixels, resizeWidth, resizeHeight, flip, rotation, 
    brightness, contrast, blur, opacity, grayscale, saturate, textLayers, croppedImage
  ]);

  // Create a separate function for generating images in different formats
  const generateImageBlob = useCallback(async (format = 'png', quality = 0.95) => {
    if (!imageUrl || !croppedAreaPixels) return null;
    
    try {
      const image = await createImage(imageUrl);
      const canvas = document.createElement('canvas');
      canvas.width = resizeWidth;
      canvas.height = resizeHeight;
      const ctx = canvas.getContext('2d');
      
      // For PNG, use transparent background; for JPEG, use white background
      if (format === 'png') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      ctx.save();
      
      // Apply transform for flip
      if (flip.horizontal || flip.vertical) {
        ctx.translate(
          flip.horizontal ? canvas.width : 0,
          flip.vertical ? canvas.height : 0
        );
        ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
      }
      
      // Apply rotation
      if (rotation !== 0) {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
      }
      
      // Apply rounded corners if needed
      if (borderRadius > 0) {
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(0, 0, canvas.width, canvas.height, borderRadius);
        } else {
          // Fallback for browsers without roundRect
          const radius = Math.min(borderRadius, canvas.width / 2, canvas.height / 2);
          ctx.moveTo(radius, 0);
          ctx.lineTo(canvas.width - radius, 0);
          ctx.quadraticCurveTo(canvas.width, 0, canvas.width, radius);
          ctx.lineTo(canvas.width, canvas.height - radius);
          ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - radius, canvas.height);
          ctx.lineTo(radius, canvas.height);
          ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.closePath();
        }
        ctx.clip();
      }
      
      // Draw the main image with filters
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) blur(${blur}px) opacity(${opacity}%) grayscale(${grayscale}%) saturate(${saturate}%)`;
      
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      
      ctx.restore();
      
      // Reset filter for overlays
      ctx.filter = 'none';
      
      // Draw border
      if (borderWidth > 0) {
        ctx.save();
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.strokeRect(borderWidth / 2, borderWidth / 2, canvas.width - borderWidth, canvas.height - borderWidth);
        ctx.restore();
      }
      
      // Draw frame
      if (frameStyle && frameStyle !== 'none') {
        ctx.save();
        switch (frameStyle) {
          case 'simple-black':
            ctx.strokeStyle = '#111';
            ctx.lineWidth = 16;
            break;
          case 'simple-white':
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 16;
            break;
          case 'gold':
            ctx.strokeStyle = 'gold';
            ctx.lineWidth = 20;
            break;
          case 'dotted':
            ctx.setLineDash([10, 8]);
            ctx.strokeStyle = '#1976d2';
            ctx.lineWidth = 12;
            break;
          default:
            break;
        }
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
        if (frameStyle === 'dotted') {
          ctx.setLineDash([]);
        }
        ctx.restore();
      }
      
      // Draw text layers
      textLayers.forEach(layer => {
        if (!layer.text) return;
        ctx.save();
        ctx.font = `${layer.size}px ${layer.font}`;
        ctx.fillStyle = layer.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.fillText(
          layer.text,
          (layer.x / 100) * canvas.width,
          (layer.y / 100) * canvas.height
        );
        ctx.restore();
      });
      
      // Draw stickers
      stickers.forEach(sticker => {
        if (!sticker.label) return;
        ctx.save();
        ctx.font = `${sticker.size}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          sticker.label, 
          (sticker.x / 100) * canvas.width, 
          (sticker.y / 100) * canvas.height
        );
        ctx.restore();
      });
      
      // Draw hashtag
      if (hashtagText) {
        ctx.save();
        ctx.font = '22px Arial';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 6;
        ctx.fillText(hashtagText, canvas.width / 2, 20);
        ctx.restore();
      }
      
      // Draw watermark
      if (watermarkText) {
        ctx.save();
        ctx.globalAlpha = watermarkOpacity / 100;
        ctx.font = '22px Arial';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 6;
        ctx.fillText(watermarkText, canvas.width / 2, canvas.height - 20);
        ctx.restore();
      }
      
      // Draw logo
      if (logoUrl) {
        try {
          const logoImg = await createImage(logoUrl);
          const logoW = logoSize;
          const logoH = logoSize;
          ctx.save();
          ctx.drawImage(
            logoImg,
            (logoPosition.x / 100) * canvas.width - logoW / 2,
            (logoPosition.y / 100) * canvas.height - logoH / 2,
            logoW,
            logoH
          );
          ctx.restore();
        } catch (e) {
          console.warn('Logo loading failed:', e);
        }
      }
      
      return new Promise((resolve) => {
        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob((blob) => {
          resolve(blob);
        }, mimeType, quality);
      });
      
    } catch (error) {
      console.error('Error generating image blob:', error);
      return null;
    }
  }, [
    imageUrl, croppedAreaPixels, rotation, flip, brightness, contrast, 
    blur, opacity, grayscale, saturate, textLayers, borderWidth, borderColor, 
    boxShadow, borderRadius, watermarkText, watermarkOpacity, resizeWidth, 
    resizeHeight, stickers, frameStyle, hashtagText, logoUrl, logoPosition, logoSize
  ]);

  const handleExport = async (type = 'png') => {
    if (!imageUrl || !croppedAreaPixels) {
      alert('Please upload and crop an image first.');
      return;
    }

    try {
      // Show loading state
      const loadingToast = document.createElement('div');
      loadingToast.textContent = `Exporting as ${type.toUpperCase()}...`;
      loadingToast.style.cssText = `
        position: fixed; top: 20px; right: 20px; background: #1976d2; color: white;
        padding: 12px 20px; border-radius: 8px; z-index: 10000; font-family: Arial;
      `;
      document.body.appendChild(loadingToast);

      const quality = type === 'jpeg' ? 0.95 : 1.0;
      const blob = await generateImageBlob(type, quality);
      
      document.body.removeChild(loadingToast);

      if (!blob) {
        alert('Failed to generate image. Please try again.');
        return;
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo-editor-export-${Date.now()}.${type}`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      // Show success message
      const successToast = document.createElement('div');
      successToast.textContent = `Successfully exported as ${type.toUpperCase()}!`;
      successToast.style.cssText = `
        position: fixed; top: 20px; right: 20px; background: #4caf50; color: white;
        padding: 12px 20px; border-radius: 8px; z-index: 10000; font-family: Arial;
      `;
      document.body.appendChild(successToast);
      setTimeout(() => {
        if (document.body.contains(successToast)) {
          document.body.removeChild(successToast);
        }
      }, 3000);

    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}. This may be due to CORS restrictions on external images. Try uploading from your computer instead.`);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageUrl(ev.target.result);
      setCroppedImage(null);
      setInputUrl('');
      pushUndo();
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    if (!inputUrl) return;
    pushUndo();
    setImageUrl(inputUrl);
    setCroppedImage(null);
  };

  const handleReset = () => {
    pushUndo();
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFlip({ horizontal: false, vertical: false });
    setBrightness(100);
    setContrast(100);
    setBlur(0);
    setOpacity(100);
    setGrayscale(0);
    setSaturate(100);
    setTextLayers([]);
    setCroppedImage(null);
  };

  // Handle state changes with undo
  const handleStateChange = (setter, value) => {
    pushUndo();
    setter(value);
  };

  const controlBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    mb: 2,
  };
  
  const controlSliderStyle = {
    width: 220,
    borderRadius: 2,
    bgcolor: '#f8fafc',
    px: 2,
  };
  
  const controlButtonStyle = {
    borderRadius: 3,
    fontWeight: 600,
    fontSize: 15,
    minWidth: 120,
    height: 40,
    boxShadow: '0 2px 8px #1976d210',
    transition: 'background 0.2s, box-shadow 0.2s',
    '&:hover': { boxShadow: '0 4px 16px #1976d220' },
  };

  // Add drag handlers for text layers and stickers
  const handleDragStart = (e, id) => {
    e.stopPropagation();
    const isTouch = e.type === 'touchstart';
    const box = e.target.closest('.image-box').getBoundingClientRect();
    const onMove = (moveEvent) => {
      const moveX = isTouch ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = isTouch ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const relX = ((moveX - box.left) / box.width) * 100;
      const relY = ((moveY - box.top) / box.height) * 100;
      updateLayer(id, { x: relX, y: relY });
    };
    const onUp = () => {
      window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
      window.removeEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
    };
    window.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
    window.addEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
  };

  const handleImageBoxDoubleClick = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const relX = ((clientX - box.left) / box.width) * 100;
    const relY = ((clientY - box.top) / box.height) * 100;
    handleAddTextLayerAt(relX, relY);
  };

  const handleTextInputChange = (id, value) => {
    updateLayer(id, { text: value });
  };

  const handleTextInputBlur = (id) => {
    updateLayer(id, { editing: false });
  };

  const handleTextBoxClick = (e, id) => {
    e.stopPropagation();
    setSelectedLayerId(id);
    updateLayer(id, { editing: true });
  };

  const renderEditControls = () => {
    if (selectedTool === 'text' && selectedLayerId) {
      const layer = textLayers.find(l => l.id === selectedLayerId);
      if (!layer) return null;
      return (
        <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
          <Box sx={controlBoxStyle}>
            <InputLabel sx={{ mr: 1 }}>Color</InputLabel>
            <input
              type="color"
              value={layer.color}
              onChange={e => updateLayer(layer.id, { color: e.target.value })}
              style={{ width: 40, height: 40, border: 'none', background: 'none', borderRadius: 8 }}
            />
          </Box>
          <Box sx={controlBoxStyle}>
            <FontDownloadIcon sx={{ mr: 1 }} />
            <Select
              value={layer.font}
              onChange={e => updateLayer(layer.id, { font: e.target.value })}
              size="small"
              sx={{ minWidth: 140, borderRadius: 2, bgcolor: '#f8fafc' }}
            >
              {FONT_FAMILIES.map(f => (
                <MenuItem key={f} value={f} style={{ fontFamily: f }}>{f}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={controlBoxStyle}>
            <FormatSizeIcon sx={{ mr: 1 }} />
            <Slider
              value={layer.size}
              min={10}
              max={120}
              step={1}
              onChange={(_, value) => updateLayer(layer.id, { size: value })}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{layer.size}px</Typography>
          </Box>
          <Button size="small" color="error" onClick={() => {
            pushUndo();
            setTextLayers(layers => layers.filter(l => l.id !== layer.id));
          }} sx={controlButtonStyle}>
            Delete
          </Button>
        </Box>
      );
    }
    
    switch (selectedTool) {
      case 'aspect':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Aspect Ratio / Resize</Typography>
            <Select
              value={aspect === null ? '' : aspect === 'custom' ? 'custom' : aspect}
              onChange={e => {
                pushUndo();
                if (e.target.value === 'custom') {
                  setAspect('custom');
                } else {
                  setAspect(e.target.value === '' ? null : Number(e.target.value));
                }
              }}
              size="small"
              sx={{ minWidth: 140, borderRadius: 2, bgcolor: '#f8fafc', width: '100%' }}
            >
              {ASPECT_RATIOS.map(r => (
                <MenuItem key={r.label} value={r.value === null ? '' : r.value}>{r.label}</MenuItem>
              ))}
            </Select>
            {aspect === 'custom' && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Width"
                  type="number"
                  value={resizeWidth}
                  onChange={e => handleStateChange(setResizeWidth, Number(e.target.value))}
                  size="small"
                  sx={{ width: 110, borderRadius: 2, bgcolor: '#f8fafc' }}
                />
                <TextField
                  label="Height"
                  type="number"
                  value={resizeHeight}
                  onChange={e => handleStateChange(setResizeHeight, Number(e.target.value))}
                  size="small"
                  sx={{ width: 110, borderRadius: 2, bgcolor: '#f8fafc' }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (resizeWidth > 0 && resizeHeight > 0) {
                      pushUndo();
                      setAspect(resizeWidth / resizeHeight);
                    }
                  }}
                  sx={{ minWidth: 150, height: 40 }}
                >
                  Set Aspect
                </Button>
              </Box>
            )}
          </Box>
        );
      case 'crop':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => handleStateChange(setZoom, value)}
              sx={controlSliderStyle}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={showCroppedImage} sx={controlButtonStyle}>
                Crop
              </Button>
              <Button variant="outlined" onClick={handleReset} sx={controlButtonStyle}>
                Reset
              </Button>
            </Box>
          </Box>
        );
      case 'rotate':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Rotate</Typography>
            <Slider
              value={rotation}
              min={-180}
              max={180}
              step={1}
              onChange={(_, value) => handleStateChange(setRotation, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{rotation}°</Typography>
          </Box>
        );
      case 'flip':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant={flip.horizontal ? 'contained' : 'outlined'}
              onClick={() => {
                pushUndo();
                setFlip(f => ({ ...f, horizontal: !f.horizontal }));
              }}
              sx={controlButtonStyle}
            >
              Flip Horizontal
            </Button>
            <Button
              variant={flip.vertical ? 'contained' : 'outlined'}
              onClick={() => {
                pushUndo();
                setFlip(f => ({ ...f, vertical: !f.vertical }));
              }}
              sx={controlButtonStyle}
            >
              Flip Vertical
            </Button>
          </Box>
        );
      case 'brightness':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Brightness</Typography>
            <Slider
              value={brightness}
              min={50}
              max={200}
              step={1}
              onChange={(_, value) => handleStateChange(setBrightness, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{brightness}%</Typography>
          </Box>
        );
      case 'contrast':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Contrast</Typography>
            <Slider
              value={contrast}
              min={50}
              max={200}
              step={1}
              onChange={(_, value) => handleStateChange(setContrast, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{contrast}%</Typography>
          </Box>
        );
      case 'blur':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Blur</Typography>
            <Slider
              value={blur}
              min={0}
              max={10}
              step={0.1}
              onChange={(_, value) => handleStateChange(setBlur, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{blur}px</Typography>
          </Box>
        );
      case 'opacity':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Opacity</Typography>
            <Slider
              value={opacity}
              min={0}
              max={100}
              step={1}
              onChange={(_, value) => handleStateChange(setOpacity, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{opacity}%</Typography>
          </Box>
        );
      case 'grayscale':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Grayscale</Typography>
            <Slider
              value={grayscale}
              min={0}
              max={100}
              step={1}
              onChange={(_, value) => handleStateChange(setGrayscale, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{grayscale}%</Typography>
          </Box>
        );
      case 'saturate':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Saturation</Typography>
            <Slider
              value={saturate}
              min={0}
              max={300}
              step={1}
              onChange={(_, value) => handleStateChange(setSaturate, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{saturate}%</Typography>
          </Box>
        );
      case 'text':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            {textLayers.length === 0 && <Typography variant="caption">No text layers. Double-click on image to add text.</Typography>}
            {textLayers.map(layer => (
              <Box key={layer.id} sx={{ border: selectedLayerId === layer.id ? '2px solid #1976d2' : '1px solid #ccc', borderRadius: 2, p: 1, mb: 2, background: selectedLayerId === layer.id ? '#e3f2fd' : '#fff', display: 'flex', flexDirection: 'column', gap: 1 }} onClick={() => setSelectedLayerId(layer.id)}>
                <TextField
                  label="Text"
                  value={layer.text}
                  onChange={e => updateLayer(layer.id, { text: e.target.value })}
                  fullWidth
                  sx={{ mb: 1, borderRadius: 2, bgcolor: '#f8fafc' }}
                />
              </Box>
            ))}
          </Box>
        );
      case 'border':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Border Width</Typography>
            <Slider
              value={borderWidth}
              min={0}
              max={40}
              step={1}
              onChange={(_, value) => handleStateChange(setBorderWidth, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{borderWidth}px</Typography>
            <Box sx={controlBoxStyle}>
              <InputLabel sx={{ mr: 1 }}>Color</InputLabel>
              <input
                type="color"
                value={borderColor}
                onChange={e => {
                  pushUndo();
                  setBorderColor(e.target.value);
                }}
                style={{ width: 40, height: 40, border: 'none', background: 'none', borderRadius: 8 }}
              />
            </Box>
          </Box>
        );
      case 'shadow':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Shadow</Typography>
            <Slider
              value={boxShadow}
              min={0}
              max={40}
              step={1}
              onChange={(_, value) => handleStateChange(setBoxShadow, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{boxShadow}px</Typography>
          </Box>
        );
      case 'round':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Corner Radius</Typography>
            <Slider
              value={borderRadius}
              min={0}
              max={100}
              step={1}
              onChange={(_, value) => handleStateChange(setBorderRadius, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{borderRadius}px</Typography>
          </Box>
        );
      case 'watermark':
        return (
          <Box sx={{ mt: 2, width: 340, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 8px #1976d210', mb: 2 }}>
            <Typography gutterBottom>Watermark Text</Typography>
            <TextField
              value={watermarkText}
              onChange={e => {
                pushUndo();
                setWatermarkText(e.target.value);
              }}
              fullWidth
              sx={{ mb: 2, borderRadius: 2, bgcolor: '#f8fafc' }}
            />
            <Typography gutterBottom>Opacity</Typography>
            <Slider
              value={watermarkOpacity}
              min={0}
              max={100}
              step={1}
              onChange={(_, value) => handleStateChange(setWatermarkOpacity, value)}
              sx={controlSliderStyle}
            />
            <Typography variant="caption">{watermarkOpacity}%</Typography>
          </Box>
        );
      case 'sticker':
        const STICKER_SET = [
          { type: 'smile', label: '😊' },
          { type: 'star', label: '⭐' },
          { type: 'heart', label: '❤️' },
          { type: 'fire', label: '🔥' },
          { type: 'thumb', label: '👍' },
        ];
        return (
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>Stickers</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {STICKER_SET.map(sticker => (
                <Button
                  key={sticker.type}
                  variant="outlined"
                  onClick={() => {
                    pushUndo();
                    const id = uuidv4();
                    setStickers(arr => [...arr, { id, type: sticker.type, label: sticker.label, x: 50, y: 50, size: 48 }]);
                    setSelectedStickerId(id);
                  }}
                  sx={{ fontSize: 28 }}
                >
                  {sticker.label}
                </Button>
              ))}
            </Box>
          </Box>
        );
      case 'frame':
        const FRAME_STYLES = [
          { key: 'none', label: 'None' },
          { key: 'simple-black', label: 'Simple Black' },
          { key: 'simple-white', label: 'Simple White' },
          { key: 'gold', label: 'Gold' },
          { key: 'dotted', label: 'Dotted' },
        ];
        return (
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>Frame</Typography>
            <Select
              value={frameStyle}
              onChange={e => handleStateChange(setFrameStyle, e.target.value)}
              size="small"
              sx={{ minWidth: 160 }}
            >
              {FRAME_STYLES.map(f => (
                <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
              ))}
            </Select>
          </Box>
        );
      case 'hashtag':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>Hashtag</Typography>
            <TextField
              label="#Hashtag"
              value={hashtagText}
              onChange={e => {
                pushUndo();
                setHashtagText(e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
          </Box>
        );
      case 'logo':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>Logo</Typography>
            <TextField
              label="Logo Image URL"
              value={logoUrl}
              onChange={e => {
                pushUndo();
                setLogoUrl(e.target.value);
              }}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <Slider
              value={logoSize}
              min={20}
              max={200}
              step={1}
              onChange={(_, value) => handleStateChange(setLogoSize, value)}
              sx={{ width: 180, mb: 1 }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const filterString = `brightness(${brightness}%) contrast(${contrast}%) blur(${blur}px) opacity(${opacity}%) grayscale(${grayscale}%) saturate(${saturate}%)`;
  const transformString = `${flip.horizontal ? 'scaleX(-1)' : ''} ${flip.vertical ? 'scaleY(-1)' : ''}`;

  return (
    <Box sx={{
      display: 'flex',
      height: '100vh',
      minHeight: '100vh',
      bgcolor: 'linear-gradient(135deg, #f8fafc 0%, #e3f0ff 100%)',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Sidebar */}
      <Box sx={{
        width: 280,
        bgcolor: '#fff',
        borderRight: 'none',
        borderRadius: '24px',
        boxShadow: '0 8px 32px #1976d220, 0 1.5px 0 #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        mx: 3,
        minHeight: 600,
        height: 'auto',
        maxHeight: 800,
        transition: 'box-shadow 0.2s',
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, color: '#1976d2', letterSpacing: 1, fontFamily: 'Montserrat, Arial', textTransform: 'uppercase', fontSize: 18 }}>
          Photo Editor
        </Typography>
        <List sx={{ width: '100%', p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, width: '100%' }}>
            {EDIT_OPTIONS.map((option) => (
              <ListItem key={option.key} disablePadding sx={{ width: '100%', m: 0 }}>
                <ListItemButton
                  selected={selectedTool === option.key && option.key !== 'undo' && option.key !== 'redo'}
                  onClick={() => {
                    if (option.key === 'undo') handleUndo();
                    else if (option.key === 'redo') handleRedo();
                    else setSelectedTool(option.key);
                  }}
                  sx={{
                    flexDirection: 'column',
                    py: 1.2,
                    width: '100%',
                    borderRadius: 2,
                    minHeight: 56,
                    bgcolor: selectedTool === option.key && option.key !== 'undo' && option.key !== 'redo' ? '#e3f2fd' : 'transparent',
                    boxShadow: selectedTool === option.key && option.key !== 'undo' && option.key !== 'redo' ? '0 1px 4px #1976d210' : 'none',
                    transition: 'background 0.2s, box-shadow 0.2s',
                    '&:hover': { bgcolor: '#f0f7ff', boxShadow: '0 1px 4px #1976d210' },
                    opacity: (option.key === 'undo' && undoStack.length === 0) || (option.key === 'redo' && redoStack.length === 0) ? 0.4 : 1,
                    pointerEvents: (option.key === 'undo' && undoStack.length === 0) || (option.key === 'redo' && redoStack.length === 0) ? 'none' : 'auto',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mb: 0.5, fontSize: 22, color: selectedTool === option.key ? '#1976d2' : '#888', transition: 'color 0.2s', justifyContent: 'center', display: 'flex' }}>{option.icon}</ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="caption" sx={{ fontSize: 11, color: selectedTool === option.key ? '#1976d2' : '#444', fontWeight: 600, textAlign: 'center' }}>{option.label}</Typography>}
                    sx={{ textAlign: 'center', width: '100%', m: 0, p: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        </List>
      </Box>
      
      {/* Main Editor Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 0 }}>
        <Box sx={{
          width: 700,
          maxWidth: '98vw',
          mb: 3,
          bgcolor: '#fff',
          borderRadius: 5,
          boxShadow: '0 8px 32px #1976d220, 0 1.5px 0 #e0e0e0',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: 'auto',
          minHeight: 600,
        }}>
          {/* Upload controls */}
          <Box sx={{ mb: 3, width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
            <form onSubmit={handleUrlSubmit} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
              <TextField
                label="Image URL"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                fullWidth
                size="small"
                sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
              />
              <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 120, height: 44, borderRadius: 2, fontWeight: 700, fontSize: 16 }}>
                From URL
              </Button>
            </form>
            <label htmlFor="upload-file-input">
              <input
                accept="image/*"
                id="upload-file-input"
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button variant="outlined" component="span" sx={{ minWidth: 120, height: 44, borderRadius: 2, fontWeight: 700, fontSize: 16 }}>
                From Computer
              </Button>
            </label>
          </Box>
          
          {/* Image Box */}
          <Box className="image-box" sx={{
            position: 'relative',
            width: 620,
            height: 420,
            background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fafc 100%)',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 4px 24px #1976d220',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid #e0e0e0',
          }}
            onDoubleClick={handleImageBoxDoubleClick}
          >
            {!imageUrl ? (
              <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
                Please upload an image from your computer or enter an image URL above.
              </Typography>
            ) : (
              <>
                {!croppedImage ? (
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={aspect || undefined}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    rotation={rotation}
                    style={{
                      containerStyle: {
                        filter: filterString,
                        transform: transformString,
                      },
                    }}
                  />
                ) : (
                  <img
                    src={croppedImage}
                    alt="Cropped"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      display: 'block', 
                      margin: '0 auto',
                      filter: filterString, 
                      transform: transformString,
                      borderRadius: borderRadius ? `${borderRadius}px` : undefined
                    }}
                  />
                )}
                
                {/* Watermark overlay */}
                {watermarkText && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      bottom: 10,
                      transform: 'translateX(-50%)',
                      color: '#fff',
                      opacity: watermarkOpacity / 100,
                      fontSize: 22,
                      fontWeight: 700,
                      pointerEvents: 'none',
                      textShadow: '0 0 6px #000',
                      zIndex: 20,
                    }}
                  >
                    {watermarkText}
                  </Box>
                )}
                
                {/* Hashtag overlay */}
                {hashtagText && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 10,
                      transform: 'translateX(-50%)',
                      color: '#fff',
                      fontSize: 22,
                      fontWeight: 700,
                      pointerEvents: 'none',
                      textShadow: '0 0 6px #000',
                      zIndex: 20,
                    }}
                  >
                    {hashtagText}
                  </Box>
                )}
                
                {/* Editable text overlays */}
                {textLayers.map(layer => (
                  <Box
                    key={layer.id}
                    sx={{
                      position: 'absolute',
                      left: `${layer.x}%`,
                      top: `${layer.y}%`,
                      transform: 'translate(-50%, -50%)',
                      color: layer.color,
                      fontFamily: layer.font,
                      fontSize: `${layer.size}px`,
                      textShadow: '0 0 4px #000',
                      pointerEvents: 'auto',
                      fontWeight: 600,
                      whiteSpace: 'pre',
                      zIndex: 10,
                      border: selectedLayerId === layer.id ? '1px dashed #1976d2' : 'none',
                      cursor: 'move',
                      userSelect: 'none',
                      minWidth: '40px',
                      minHeight: '32px',
                      background: layer.editing ? 'rgba(255,255,255,0.1)' : 'none',
                    }}
                    onMouseDown={e => handleDragStart(e, layer.id)}
                    onTouchStart={e => handleDragStart(e, layer.id)}
                    onClick={e => handleTextBoxClick(e, layer.id)}
                  >
                    {layer.editing ? (
                      <input
                        autoFocus
                        value={layer.text}
                        onChange={e => handleTextInputChange(layer.id, e.target.value)}
                        onBlur={() => handleTextInputBlur(layer.id)}
                        style={{
                          font: 'inherit',
                          color: 'inherit',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          width: '100%',
                          textAlign: 'center',
                        }}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => { if (e.key === 'Enter') handleTextInputBlur(layer.id); }}
                      />
                    ) : (
                      <>
                        {layer.text || <span style={{ opacity: 0.5 }}>[Click to edit]</span>}
                        {selectedLayerId === layer.id && (
                          <Button
                            size="small"
                            color="error"
                            sx={{
                              ml: 1,
                              minWidth: 0,
                              fontSize: 18,
                              p: 0,
                              position: 'absolute',
                              top: -18,
                              right: -18,
                              zIndex: 20,
                              borderRadius: '50%',
                              minHeight: 0,
                              height: 28,
                              width: 28,
                              lineHeight: 1,
                              boxShadow: '0 1px 4px #1976d210',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={ev => { 
                              ev.stopPropagation(); 
                              pushUndo();
                              setTextLayers(layers => layers.filter(l => l.id !== layer.id)); 
                              setSelectedLayerId(null); 
                            }}
                          >
                            ×
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                ))}
                
                {/* Stickers overlay */}
                {stickers.map(sticker => (
                  <Box
                    key={sticker.id}
                    sx={{
                      position: 'absolute',
                      left: `${sticker.x}%`,
                      top: `${sticker.y}%`,
                      transform: 'translate(-50%, -50%)',
                      fontSize: `${sticker.size}px`,
                      cursor: 'move',
                      userSelect: 'none',
                      zIndex: 15,
                      border: selectedStickerId === sticker.id ? '2px dashed #1976d2' : 'none',
                      borderRadius: 2,
                      p: 0.5,
                    }}
                    onClick={() => setSelectedStickerId(sticker.id)}
                  >
                    {sticker.label}
                    {selectedStickerId === sticker.id && (
                      <Button
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: -15,
                          right: -15,
                          minWidth: 20,
                          minHeight: 20,
                          borderRadius: '50%',
                          p: 0,
                          fontSize: 12,
                        }}
                        onClick={ev => { 
                          ev.stopPropagation(); 
                          pushUndo();
                          setStickers(arr => arr.filter(s => s.id !== sticker.id)); 
                          setSelectedStickerId(null); 
                        }}
                      >
                        ×
                      </Button>
                    )}
                  </Box>
                ))}
                
              </>
            )}
          </Box>
          
          {/* Controls Panel */}
          <Box sx={{ mt: 4, width: '100%', bgcolor: '#f8fafc', borderRadius: 3, boxShadow: '0 2px 8px #1976d210', p: 3, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', border: '1.5px solid #e0e0e0' }}>
            {renderEditControls()}
            {/* Export buttons */}
            {imageUrl && croppedAreaPixels && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={() => handleExport('png')} sx={{ minWidth: 160 }}>
                  Export as PNG
                </Button>
                <Button variant="outlined" color="primary" onClick={() => handleExport('jpeg')} sx={{ minWidth: 160 }}>
                  Export as JPEG
                </Button>
              </Box>
            )}
          </Box>
          
          {/* Download/Reset Actions */}
          {croppedImage && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                href={croppedImage}
                download="cropped.jpg"
                sx={{ minWidth: 180, fontWeight: 600 }}
              >
                Download Cropped Image
              </Button>
              <Button variant="outlined" onClick={handleReset} sx={{ minWidth: 120 }}>
                Edit Again
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PhotoEditor;
